import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Route guard for user-authenticated pages (JWT httpOnly-cookie auth).
 *
 * Fixes the flash-of-protected-content / redirect race that the per-page
 * `if (!user) navigate('/login')` checks caused: those fired during the
 * initial `/auth/me` resolution (when `loading` is true and `user` is still
 * null), bouncing authenticated users to /login on first paint.
 *
 * Behaviour:
 * - while auth is resolving (`loading`) → render a spinner, never the page;
 * - resolved + no user → redirect to `redirectTo` (preserving `from` so the
 *   login page can send the user back);
 * - resolved + user → render the protected children.
 *
 * NOTE: admin pages (/admin*) self-gate via HTTP Basic and must NOT use this.
 */
export const RequireAuth = ({ children, redirectTo = "/login" }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="require-auth-loading"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-500 text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location.pathname + location.search }}
        data-testid="require-auth-redirect"
      />
    );
  }

  return children;
};
